// vueui/src/stores/auth.ts

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getLocalUserEmail,
  getLocalUserRole,
  isSignedIn,
  clearToken,
  saveToken,
  saveRememberMe,
  getToken
} from '@/assets/common'
import { getApiUrl, API_ENDPOINTS, apiRequest } from '@/assets/api'
import type { Router } from 'vue-router'
import { Role, isSuperUser as checkIsSuperUser } from '../../../shared/utils/roles'
import { cleanupOnLogout } from '@/utils/cleanup'

export const useAuthStore = defineStore('auth', () => {
  const signedIn = ref(isSignedIn())
  const email = ref(getLocalUserEmail())
  const role = ref(getLocalUserRole()?.[0] || null) // Get primary role for backward compatibility

  const userRoleLabel = computed(() => {
    if (role.value === Role.SuperAdmin) return 'SuperAdmin'
    if (role.value === Role.Admin) return 'Admin'
    if (role.value === Role.Editor) return 'Editor'
    if (role.value === Role.Author) return 'Author'
    return 'Visitor'
  })

  const isSuperUser = computed(() =>
    checkIsSuperUser(getLocalUserRole() || role.value, email.value)
  )

  const refreshAuth = () => {
    signedIn.value = isSignedIn()
    email.value = getLocalUserEmail()
    role.value = getLocalUserRole()?.[0] || null // Get primary role for backward compatibility
  }

  const logout = async (router: Router) => {
    try {
      // Call backend to log sign-out event
      const logoutUrl = getApiUrl(API_ENDPOINTS.AUTH.LOGOUT)
      await apiRequest(logoutUrl, {
        method: 'POST'
      })
    } catch (error) {
      // Continue with logout even if audit logging fails
      console.warn('Failed to log sign-out event:', error)
    }
    
    // Clear client-side state
    clearToken()
    // Clear intended path when logging out
    sessionStorage.removeItem('intendedPath')
    signedIn.value = false
    email.value = null
    role.value = null
    
    // Clean up any running processes (like PDF polling)
    cleanupOnLogout()
    
    if (router) router.push({ name: 'SignIn' })
  }

  const clearAuth = () => {
    clearToken()
    signedIn.value = false
    email.value = null
    role.value = null
  }

  const login = async (
    emailInput: string,
    passwordInput: string,
    rememberMe: boolean
  ) => {
    const url = getApiUrl(API_ENDPOINTS.AUTH.LOGIN)
    const response = await apiRequest(url, {
      method: 'POST',
      body: { email: emailInput, password: passwordInput }
    })

    const data = await response.json()

    if (!response.ok) throw new Error(data.message)

    saveToken(data.token, rememberMe)
    saveRememberMe(emailInput, rememberMe)
    refreshAuth()
  }

  return {
    signedIn,
    email,
    role,
    userRoleLabel,
    isSuperUser,
    refreshAuth,
    logout,
    clearAuth,
    login,
  }
})
